import { Menu, MenuSection } from '../../../types/menu'

interface MenuPreviewProps {
  menu: Menu
}

export const MenuPreview = ({ menu }: MenuPreviewProps) => {
  const theme = menu.theme
  const layout = menu.layout || '1col'
  const isTwoColumn = layout !== '1col'

  // Determine grid template based on layout
  const getGridTemplate = () => {
    switch (layout) {
      case '2col': return 'repeat(2, 1fr)'
      case '2col-left': return '3fr 2fr'
      case '2col-right': return '2fr 3fr'
      default: return '1fr'
    }
  }

  // Helper function to distribute sections into columns
  const distributeSections = (): MenuSection[][] => {
    if (!isTwoColumn) {
      return [menu.sections]
    }

    const columns: MenuSection[][] = [[], []]

    // Place sections based on their column assignment
    menu.sections.forEach((section) => {
      const column = section.column || 'column1'
      const columnIndex = column === 'column2' ? 1 : 0
      columns[columnIndex].push(section)
    })

    return columns
  }

  const renderSection = (section: MenuSection) => (
    <div key={section.id} className="space-y-4">
      {/* Section Header */}
      <h2
        className="text-xl font-bold"
        style={{
          color: theme.colors.sectionTitle || theme.colors.primary,
          fontFamily: theme.typography.sectionTitleFont || theme.typography.fontFamily
        }}
      >
        {section.name}
      </h2>

      {/* Section Items */}
      <div className="space-y-3">
        {section.items.map((item) => (
          <div key={item.id} className="flex justify-between items-start gap-4">
            <div className="flex-1 max-w-[600px]">
              <h3
                className="font-medium"
                style={{
                  color: theme.colors.menuItemTitle || theme.colors.text,
                  fontFamily: theme.typography.menuItemTitleFont || theme.typography.fontFamily
                }}
              >
                {item.name}
              </h3>
              {item.description && (
                <p
                  className="text-sm mt-1"
                  style={{
                    color: theme.colors.description || theme.colors.secondary,
                    fontFamily: theme.typography.descriptionFont || theme.typography.fontFamily,
                    whiteSpace: 'pre-line'
                  }}
                >
                  {item.description}
                </p>
              )}
            </div>
            {item.price && (
              <span
                className="font-semibold whitespace-nowrap flex-shrink-0 ml-auto"
                style={{
                  color: theme.colors.price || theme.colors.text,
                  fontFamily: theme.typography.priceFont || theme.typography.fontFamily
                }}
              >
                {menu.currency}{String(item.price).replace(/[^\d.]/g, '')}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  )

  const columns = distributeSections()

  return (
    <div
      className="gap-24"
      style={{
        display: 'grid',
        gridTemplateColumns: getGridTemplate(),
      }}
    >
      {columns.map((columnSections, columnIndex) => (
        <div key={columnIndex} className="space-y-6">
          {columnSections.map(renderSection)}
        </div>
      ))}
    </div>
  )
}

export default MenuPreview